import type { UseQueryResult } from '@tanstack/react-query'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { useErrorHandler } from '@/features/bidding/hooks/useErrorHandler'
import { pqClient } from '@/features/prequalification/localControl/endpoints/pqClient'
import type { LocalPqRun } from '@/features/prequalification/localControl/types'
import { isTerminalPhase } from '@/features/prequalification/types'
import type { UseQueryOptions } from '@/types/queries'

export const GET_LOCAL_PQ_RESOURCE_SET_RUNS_API_ID = 'GET_LOCAL_PQ_RESOURCE_SET_RUNS'

/** Poll cadence while at least one run is still in flight. */
const LIVE_REFETCH_INTERVAL_MS = 5_000

/**
 * Live-status poll cadence: keep polling while any run is non-terminal, stop once
 * they all settle (or there is no data yet). Inverting this would either hammer the
 * API forever or freeze a live view, so it is unit-tested in isolation.
 */
export function nextRunsRefetchInterval(runs: LocalPqRun[] | undefined): number | false {
  const hasActiveRun = runs?.some((run) => !isTerminalPhase(run.phase)) ?? false
  return hasActiveRun ? LIVE_REFETCH_INTERVAL_MS : false
}

export type UseLocalPqResourceSetRunsQueryParams = {
  resourceSetId: string
}

export type UseLocalPqResourceSetRunsQueryResult = Omit<UseQueryResult, 'data'> & {
  runs: LocalPqRun[] | null
}

export function useLocalPqResourceSetRunsQuery(
  params: UseLocalPqResourceSetRunsQueryParams,
  options?: UseQueryOptions<LocalPqRun[]>,
): UseLocalPqResourceSetRunsQueryResult {
  const { data, ...queryResult } = useQuery({
    refetchInterval: (query) => nextRunsRefetchInterval(query.state.data),
    ...options,
    queryFn: () => pqClient.listResourceSetRuns(params.resourceSetId),
    queryKey: [GET_LOCAL_PQ_RESOURCE_SET_RUNS_API_ID, params],
  })

  useErrorHandler(queryResult.isError)

  return {
    ...queryResult,
    runs: data ?? null,
  }
}

export function useInvalidateLocalPqResourceSetRunsQuery() {
  const queryClient = useQueryClient()

  return () => queryClient.invalidateQueries({ queryKey: [GET_LOCAL_PQ_RESOURCE_SET_RUNS_API_ID] })
}
